/**
 * @fileoverview CSRFトークンコンテキストの定義
 *
 * このファイルでは、CSRFトークンを管理するためのReactコンテキストを定義しています。
 * アプリケーション全体でCSRFトークンを共有し、セキュアな操作を実現するための
 * 基盤となるコンポーネントです。
 */

import { createContext } from "react";

/**
 * CSRFトークンの型定義
 * 有効なトークン文字列、または未設定状態を表すundefined
 *
 * @typedef {string|undefined} CsrfTokenType
 */
export type CsrfTokenType = string | undefined;

/**
 * CSRFトークンコンテキストの型定義
 * コンテキストを通して提供される状態と操作を定義
 *
 * @typedef {Object} CsrfTokenContextType
 * @property {CsrfTokenType} csrfToken - 現在のCSRFトークン値
 * @property {() => Promise<void>} issueCsrfToken - 新しいCSRFトークンを発行する非同期関数
 * @property {boolean} loading - トークン発行処理中かどうかを示すフラグ
 * @property {Error|null} error - トークン発行処理中に発生したエラー、またはnull
 */
export type CsrfTokenContextType = {
  csrfToken: CsrfTokenType;
  issueCsrfToken: () => Promise<void>;
  loading: boolean;
  error: Error | null;
};

/**
 * CSRFトークン管理のためのReactコンテキスト
 *
 * このコンテキストにより、アプリケーション内の任意のコンポーネントから
 * CSRFトークン情報にアクセスできるようになります。
 * 初期値は空オブジェクトで、プロバイダーによって適切な値が設定されます。
 *
 * @type {React.Context<CsrfTokenContextType>}
 */
export const CsrfTokenContext = createContext<CsrfTokenContextType>(
  {} as CsrfTokenContextType
);
